import { CategoryEnumOptions } from "@boundless/bl-api-client/common/models/CategoryEnumLabels"
import { useCommonDocumentTypesList } from "@boundless/bl-api-client/common/services"
import {
  getCustomerRepositoryListQueryKey,
  useCustomerRepositoryPartialUpdate,
} from "@boundless/bl-api-client/customer/services"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { getRouteApi } from "@tanstack/react-router"
import { CircleCheckIcon, FileIcon, LoaderCircleIcon } from "lucide-react"
import { useForm } from "react-hook-form"

import {
  useDocuments,
  type CommonDocument,
} from "@/components/documents/context/DocumentsProvider"
import { DeleteDocumentDialog } from "@/components/documents/DeleteDocumentDialog"
import { PreviewDialog } from "@/components/documents/PreviewDialog"
import { FormSearchableSelect } from "@/components/form-elements/FormSearchableSelect"
import { FormSelect } from "@/components/form-elements/FormSelect"
import { Form } from "@/components/ui/form"
import { uncategorizedDocumentFormSchema } from "@/lib/schemas"
import { cn } from "@/lib/utils"

import type { z } from "zod"

type FormSchema = z.infer<typeof uncategorizedDocumentFormSchema>

export function UncategorizedDocumentEntry({ doc }: { doc: CommonDocument }) {
  const queryClient = useQueryClient()
  const routeApi = getRouteApi("/_dashboard/documents")
  const { companyUuid } = routeApi.useRouteContext()

  const { allDocuments, savedTypeIds } = useDocuments()

  const form = useForm<FormSchema>({
    resolver: zodResolver(uncategorizedDocumentFormSchema),
  })
  const category = form.watch("category")
  const type = form.watch("type")

  const { data: docTypes } = useCommonDocumentTypesList()

  const typeOptions = (docTypes || [])
    .filter((t) => t.category === category && !savedTypeIds.includes(t.id))
    .map((t) => ({
      value: t.id,
      label: t.name,
    }))

  const { mutate: confirmDocument, isPending } =
    useCustomerRepositoryPartialUpdate({
      mutation: {
        onSuccess: (data) => {
          const updatedList = allDocuments.map((doc) =>
            doc.uuid === data.uuid ? { ...doc, ...data } : doc,
          )
          const queryKey = getCustomerRepositoryListQueryKey(companyUuid)
          queryClient.setQueryData(queryKey, updatedList)
        },
      },
    })

  const onSubmit = (data: FormSchema) => {
    confirmDocument({
      companyUuid,
      uuid: doc.uuid,
      data: { isConfirmed: true, type: data.type },
    })
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        role="row"
        className={cn(
          "grid gap-3 rounded-lg border p-3",
          "md:items-center md:gap-2 md:rounded-none md:border-0 md:p-0",
          "md:grid-cols-[2fr_2fr_2fr_0.5fr_0.5fr_0.5fr]",
        )}
      >
        <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-3">
          <span className="text-muted-foreground text-base font-medium md:hidden">
            Document Name
          </span>
          <div className="flex items-center gap-3">
            <FileIcon className="h-4 w-4" />
            <span className="text-sm">{doc.filename}</span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-muted-foreground text-base font-medium md:hidden">
            Category
          </span>
          <FormSelect
            name="category"
            options={CategoryEnumOptions}
            placeholder="Select Category"
            label="Category"
            hiddenLabel
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-muted-foreground text-base font-medium md:hidden">
            Type
          </span>
          <FormSearchableSelect
            name="type"
            options={typeOptions}
            placeholder="Select Type"
          />
        </div>

        <div className="flex items-center justify-between md:block md:text-center">
          <span className="text-muted-foreground text-base font-medium md:hidden">
            Preview
          </span>
          <PreviewDialog className="h-5 w-5" doc={doc} />
        </div>

        <div className="flex items-center justify-between md:block md:text-center">
          <span className="text-muted-foreground text-base font-medium md:hidden">
            Confirm
          </span>
          {isPending ? (
            <LoaderCircleIcon className="text-primary h-5 w-5 animate-spin align-middle" />
          ) : (
            <button
              type="submit"
              className="cursor-pointer align-middle"
              disabled={!type}
            >
              <CircleCheckIcon
                className={cn(
                  "h-5 w-5",
                  type ? "text-primary" : "text-muted/30",
                )}
              />
            </button>
          )}
        </div>

        <div className="flex items-center justify-between md:block md:text-center">
          <span className="text-muted-foreground text-base font-medium md:hidden">
            Remove
          </span>
          <DeleteDocumentDialog className="h-5 w-5" doc={doc} />
        </div>
      </form>
    </Form>
  )
}
